import {
  getConnectionKeysUrl,
  getLinkedServicesUrl,
  getAllConnectionKeysUrl,
  updateConnectionKeyById,
  addConnectionKeyUrl,
  testConnectionKeyUrl,
  decryptConnectionKeyFieldUrl,
  getConnectionKeyBackupUrl,
  reactivateConnectionKeyUrl,
} from "./constants";
import base from "../middlewares/interceptors";

interface ConnectionKey {
  id: string;
  name: string;
  code: string;
}
export const getConnectionKeys = async (
  type?: string,
  id?: string,
  system_id?: number,
): Promise<ConnectionKey[]> => {
  try {
    let response;

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (type) {
      queryParams.append("connection_type", type);
    }
    if (system_id) {
      queryParams.append("system_id", system_id.toString());
    }
    if (id) {
      queryParams.append("connection_key_filter_ids", id);
    }
    // Construct the URL
    const queryString = queryParams.toString();
    // ✅ PREVENT UNOPTIMIZED CALLS: Don't call API without parameters
    if (!queryString) {
      console.warn(
        "getConnectionKeys called without parameters - returning empty array to prevent unoptimized API call",
      );
      return [];
    }
    const url = `${getConnectionKeysUrl}?${queryString}`;

    response = await base.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching connection keys:", error);
    return [];
  }
};
export const getConnectionKeysByFilter = async (
  type?: string,
  id?: string,
  system_id?: number,
): Promise<ConnectionKey[]> => {
  try {
    let response;

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (type) {
      queryParams.append("connection_type", type);
    }
    if (system_id && system_id > 0) {
      queryParams.append("system_id", system_id.toString());
    }
    if (id) {
      queryParams.append("connection_key_filter_ids", id);
    }
    // Construct the URL
    const queryString = queryParams.toString();
    // ✅ PREVENT UNOPTIMIZED CALLS: Don't call API without parameters
    if (!queryString) {
      console.warn(
        "getConnectionKeysByFilter called without parameters - returning empty array to prevent unoptimized API call",
      );
      return [];
    }
    const url = `${getConnectionKeysUrl}?${queryString}`;

    response = await base.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching connection keys:", error);
    return [];
  }
};
export const getConnectionKeysPaginatedServices = async (
  page: any,
  pSize: any,
  isActive: string = "true",
) => {
  try {
    const pageN = page == null ? process.env.REACT_APP_DEFAULT_PAGE : page;
    const pageS =
      pSize == null ? process.env.REACT_APP_DEFAULT_PAGE_SIZE : pSize;
    let requestUrl = `${getConnectionKeysUrl}/all/pages?is_active=${isActive}&page=${pageN}&size=${pageS}`;
    const response = await base.get(requestUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};
export const getAllPaginatedConnectionKeys = async (
  page: any,
  pSize: any,
  searchTerm?: string,
  signal?: AbortSignal,
  isActive?: string,
) => {
  try {
    const pageN = page == null ? process.env.REACT_APP_DEFAULT_PAGE : page;
    const pageS =
      pSize == null ? process.env.REACT_APP_DEFAULT_PAGE_SIZE : pSize;

    // Build base URL with pagination
    let requestUrl = `${getAllConnectionKeysUrl}/pages?page=${pageN}&size=${pageS}`;

    // Add isActive filter if provided (undefined means "All")
    if (isActive !== undefined) {
      requestUrl += `&is_active=${isActive}`;
    }

    // Add search parameter if searchTerm is provided and has minimum 3 characters
    if (searchTerm && searchTerm.trim().length >= 3) {
      requestUrl += `&name=${encodeURIComponent(searchTerm.trim())}`;
    }

    const response = await base.get(requestUrl, {
      signal, // Add abort signal for request cancellation
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getAllConnectionKeys = async (connectionKeyIds: any) => {
  try {
    const connectionKeyIdsQueryParam = connectionKeyIds.join(",");
    const apiUrl = `${getAllConnectionKeysUrl}?connection_key_filter_ids=${connectionKeyIdsQueryParam}`;
    const response = await base.get(apiUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};
export const getAllConnectionKeysWithoutId = async () => {
  try {
    const apiUrl = `${getAllConnectionKeysUrl}`;
    const response = await base.get(apiUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getConnectionKeysByLinkedService = async (
  linked_service_id: number,
) => {
  try {
    const response = await base.get(
      `${getLinkedServicesUrl}/${linked_service_id}/connection-keys-details`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getConnectionKeyDetail = async ({
  currentConnectionKeyId,
}: any) => {
  try {
    const response = await base.get(
      `${getConnectionKeysUrl}/${currentConnectionKeyId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const addConnectionKey = async ({ payload }: any) => {
  try {
    const response = await base.post(addConnectionKeyUrl, payload);
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const updateConnectionKey = async ({
  currentConnectionKeyId,
  payload,
}: any) => {
  try {
    // Extract comment from payload to use as query parameter
    const comment = payload?.comment?.trim();

    // Remove comment from the payload body
    const { comment: _, ...payloadWithoutComment } = payload;

    // Build request URL with comment as query parameter if present
    const requestUrl = comment
      ? `${updateConnectionKeyById}/${currentConnectionKeyId}?comment=${encodeURIComponent(comment)}`
      : `${updateConnectionKeyById}/${currentConnectionKeyId}`;

    const response = await base.put(requestUrl, payloadWithoutComment);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const deleteConnectionKey = async (currentConnectionKeyId: number) => {
  try {
    const response = await base.delete(
      `${updateConnectionKeyById}/${currentConnectionKeyId}`,
    );
    return { response: response.data, status: response.status };
  } catch (error) {
    console.error(error);
  }
};

export const testConnectionKey = async ({ payload }: any) => {
  try {
    const response = await base.post(testConnectionKeyUrl, payload);
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};
export const getConnectionKeyBackup = async ({
  connectionKeyId,
  page,
  pSize,
}: any) => {
  try {
    const pageN = page == null ? process.env.REACT_APP_DEFAULT_PAGE : page;
    const pageS =
      pSize == null ? process.env.REACT_APP_DEFAULT_PAGE_SIZE : pSize;

    let requestUrl;
    if (connectionKeyId && connectionKeyId !== 0) {
      requestUrl = `${getConnectionKeyBackupUrl}?connection_key_backup_filter_ids=${connectionKeyId}&page=${pageN}&size=${pageS}`;
    }
    const response = await base.get(requestUrl);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const decryptConnectionKeyField = async (value: any) => {
  try {
    const response = await base.get(
      `${decryptConnectionKeyFieldUrl}?value=${value}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error decrypting connection key field:", error);
    return Promise.reject(error);
  }
};

/**
 * Reactivate connection key
 * @param connectionKeyId - The ID of the connection key to reactivate
 * @returns Promise with reactivation result
 */
export const reactivateConnectionKey = async (
  connectionKeyId: string | number,
) => {
  try {
    const response = await base.put(
      `${reactivateConnectionKeyUrl}/${connectionKeyId}/reactivate`,
    );
    return response.data;
  } catch (error) {
    console.error("Error reactivating connection key:", error);
    throw error;
  }
};
